<template>
  <div
    :class="fonts[config.style.font]"
    :style="{
      backgroundColor: `#${secondaryColor}`,
    }"
  >
    <div class="flex justify-center relative">
      <div
        class="absolute right-0 h-[20rem] w-full rounded-b-3xl"
        :style="{
          'background-color': `#${primaryColor}`,
        }"
      />

      <div class="pb-16 z-1 w-full">
        <div class="w-full py-4 px-2">
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="text-white text-center text-3xl sm:text-4xl pb-4 font-bold"
            v-html="config.header?.text"
          />

          <div
            class="text-white text-center text-xl"
            v-html="config.header?.subtext"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <div class="flex justify-center px-8">
          <div class="w-full bg-white rounded-3xl shadow-xl h-full">
            <div class="p-8 grid space-y-8 flex justify-center">
              <Component
                :is="activeStepComponent()"
                v-if="activeStepComponent()"
                :config="config"
                :quiz-config="quizConfig"
                :exit-campaign-subs="exitCampaignSubs"
                :can-submit="canSubmit"
                :loading="isLoadingOfferWall"
                :co-reg-campaign-subs="coRegCampaignSubs"
                @submit="submitStep()"
              />
            </div>
          </div>
        </div>

        <div class="text-center text-sm pt-4">
          <div class="inline-flex">
            <p
              class="text-gray-500"
            >
              {{ __('co_reg_lander_002_label_data_secure') }}
            </p>

            <a
              class="underline mx-2"
              :href="config.footer?.privacy_link ?? '/privacy'"
              target="_blank"
              :style="{
                color: `#${primaryColor}`,
              }"
            >
              <p>
                - {{ __('co_reg_lander_002_text_privacy') }} -
              </p>
            </a>

            <!-- eslint-disable vue/no-v-html -->
            <div
              class="text-gray-500"
              v-html="__('label_required_info')"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {defineProps, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import EmailForm from '@timon/js/Templates/CO-REG-LANDER-002/Components/EmailForm.vue';
import NameForm from '@timon/js/Templates/CO-REG-LANDER-002/Components/NameForm.vue';
import GenderBirthForm from '@timon/js/Templates/CO-REG-LANDER-002/Components/GenderBirthForm.vue';
import CoRegForm from '@timon/js/Templates/CO-REG-LANDER-002/Components/CoRegForm.vue';
import axios from 'axios';
import Exit from '@timon/js/Templates/CO-REG-LANDER-002/Components/Exit.vue';
import {
  checkIfTargetingCookiesAreAccepted,
  fingerprint,
  isPreview,
  registerVisit,
} from '@timon/js/Mixins/helpers';
import {useStore} from 'vuex';
import {pushDataLayer} from '@global/js/Mixins/helpers';

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },

  quizConfig: {
    type: Object,
    required: true,
  },

  variables: {
    type: Object,
    required: true,
  },

  collectorFlowUuid: {
    type: String,
    required: true,
  },

  adUrlUuid: {
    type: String,
    required: false,
    default: null,
  },

  iframeDomain: {
    type: String,
    required: false,
    default: null,
  },

  campaignSubId: {
    type: [Number, null],
    required: false,
    default: null,
  },

  fingerprintRequired: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const fonts = {
  'mono': 'font-mono',
  'sans': 'font-sans',
  'serif': 'font-serif',
};

const activeStepIndex = ref(0);
watch(() => activeStepIndex.value, () => {
  nextTick(() => {
    resize();
  });
});

onMounted(() => {
  resize();
});

const resize = () => {
  if (window && window.parent) {
    const message = {
      type: 'resize',
      height: document.body.scrollHeight,
    };
    window.parent.postMessage(
      message,
      props.iframeDomain,
    );
  }
};

const form = {
  gender: null,
  birth_date: null,
  email: null,
  selected_co_reg_campaign_subs: [],
  first_name: null,
  last_name: null,
  postcode: null,
  terms_accepted: false,
};

const primaryColor = ref(props.config.style?.primary);
const secondaryColor = ref(props.config.style?.secondary);

const coRegCampaignSubs = ref([]);
const exitCampaignSubs = ref([]);

const landingParticipantUuid = ref(null);
const landingVisitUuid = ref(null);
const isLoadingOfferWall = ref(false);

const fingerprintId = ref(null);
const canSubmit = ref(true);

const store = useStore();

store.commit('errors/setErrors', []);
store.commit('form/setForm', form);

function acceptedCookies() {
  checkIfTargetingCookiesAreAccepted(() => registerVisit(props.adUrlUuid));
}

onMounted(() => {
  window.addEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.addEventListener('CookieScriptAccept', acceptedCookies);

  if (!isPreview()) {
    registerVisit(props.adUrlUuid, (data) => {
      landingVisitUuid.value = data.visit_key;
    });
  }
});

onUnmounted(() => {
  window.removeEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.removeEventListener('CookieScriptAccept', acceptedCookies);
});

function nextStep() {
  activeStepIndex.value += 1;

  pushDataLayer({
    event: 'next_step',
    step_key: activeStepIndex.value,
  });
}

function activeStepComponent() {
  switch (activeStepIndex.value) {
    case 0:
      return GenderBirthForm;
    case 1:
      return EmailForm;
    case 2:
      return CoRegForm;
    case 3:
      return NameForm;
    case 4:
      return Exit;
    default:
      return null;
  }
}

async function submitStep() {
  canSubmit.value = false;
  store.commit('errors/clearErrors');
  isLoadingOfferWall.value = true;

  let isFinalStep = false;
  let includeOfferWall = false;
  let includeExitWall = false;

  if (props.fingerprintRequired && activeStepIndex.value === 0) {
    if (!isPreview() && !fingerprintId.value) {
      try {
        const fp = await fingerprint();
        const result = await fp.get();
        fingerprintId.value = result.visitorId;
      } catch (error) {
        console.error(error);
      }
    }
  }

  if (activeStepIndex.value === 1) {
    includeOfferWall = true;
  }

  if (activeStepIndex.value === 3) {
    includeExitWall = true;
    isFinalStep = true;
  }

  if (isPreview()) {
    axios.post(route('zazu.v1.landing.form.submit-preview', {
      include_offer_wall: includeOfferWall,
    }), {
      include_exit_wall: includeExitWall,
      campaignSub: props.campaignSubId,
    })
      .then((response) => {
        canSubmit.value = true;
        if (!!response.data.offer_wall && response.data.offer_wall.length > 0) {
          coRegCampaignSubs.value = response.data.offer_wall;
        }
        exitCampaignSubs.value = response.data.exit_wall;

        isLoadingOfferWall.value = false;

        nextStep();
      });

    return;
  }

  axios.post(route(isFinalStep ? 'zazu.v1.landing.form.submit' : 'zazu.v1.landing.form.validate', {
    adUrl: props.adUrlUuid,
    step: activeStepIndex.value,
  }), {
    is_final_step: isFinalStep,
    include_offer_wall: includeOfferWall,
    include_exit_wall: includeExitWall,
    landing_visit_uuid: landingVisitUuid.value,
    fingerprint: fingerprintId.value,
    selected: store.state.form.selected_co_reg_campaign_subs,

    ...store.state.form,
  })
    .then((response) => {
      if (!!response.data.offer_wall && response.data.offer_wall.length > 0) {
        coRegCampaignSubs.value = response.data.offer_wall;
      }
      exitCampaignSubs.value = response.data.exit_wall;
      landingParticipantUuid.value = response.data.landing_participant_uuid;

      isLoadingOfferWall.value = false;

      nextStep();

      canSubmit.value = true;
    })
    .catch((error) => {
      if (error.response.data.errors) {
        store.commit('errors/setErrors', error.response.data.errors);
      }

      canSubmit.value = true;
    });
}
</script>
